import { useState, useEffect, useCallback, useRef } from "react";
import { FiMapPin, FiZoomIn, FiPlus, FiX, FiLoader, FiTrash2, FiCheck, FiEdit3, FiUpload, FiClipboard, FiImage, FiCamera } from "react-icons/fi";
import { BaseUrl } from "../../../../../../../utilities/BaseUrl";
import GoogleMapPreview from "../../../../../../../components/GoogleMapPreview";
import ImagePreview from "../../../../../../../components/images/ImagePreview";
import { PopImageService } from "../../../../../../../services/PopImageService";
import { FormLocationPopUpdate } from "./FormLocationUpdate";
import { formatSize } from "../../../../../../../utilities/image_configuration/formatSize";
import { compressImages } from "../../../../../../../utilities/image_configuration/compress_images";

export const LocationAndImagePopSection = ({ location, images, popId, refreshData }) => {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [openLightbox, setOpenLightbox] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [compressInfo, setCompressInfo] = useState([]);

  const [isEditLocation, setIsEditLocation] = useState(false);

  const [loadingDelete, setLoadingDelete] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [targetDeleteId, setTargetDeleteId] = useState(null);

  // Refs
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const CARD_STYLE = { background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)", backdropFilter: "blur(12px)" };
  const MAX_IMAGES = 3;
  const currentImages = images?.length || 0;
  const remainingSlots = MAX_IMAGES - currentImages;

  // Cleanup URL object
  useEffect(() => {
    return () => {
      previews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previews]);

  // ============================================
  // PROCESS FILES DENGAN AUTO-COMPRESS
  // ============================================
  const processFiles = useCallback(async (files) => {
    const validFiles = Array.from(files).filter(file => file.type.startsWith("image/"));
    if (validFiles.length === 0) { alert("Tidak ada gambar yang valid"); return; }

    const availableSlots = remainingSlots - selectedFiles.length;
    if (availableSlots <= 0) { alert("Kuota unggah sudah penuh"); return; }

    const filesToProcess = validFiles.slice(0, availableSlots);
    setCompressing(true);

    try {
      const compressedResults = await compressImages(filesToProcess);
      if (compressedResults.length === 0) { alert("Gagal mengompres gambar"); setCompressing(false); return; }

      const newFiles = compressedResults.map(r => r.file);
      const newPreviews = newFiles.map(file => URL.createObjectURL(file));
      const newInfo = compressedResults.map(r => ({
        originalSize: r.originalSize,
        compressedSize: r.compressedSize,
        saved: r.originalSize - r.compressedSize,
        percent: Math.round(((r.originalSize - r.compressedSize) / r.originalSize) * 100)
      }));

      setSelectedFiles(prev => [...prev, ...newFiles]);
      setPreviews(prev => [...prev, ...newPreviews]);
      setCompressInfo(prev => [...prev, ...newInfo]);
    } catch (error) {
      console.error("Compression error:", error);
      alert("Gagal memproses gambar");
    } finally {
      setCompressing(false);
    }
  }, [remainingSlots, selectedFiles.length]);

  // ============ DRAG AND DROP ============
  const handleDragEnter = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setDragCounter(prev => {
      const n = prev - 1;
      if (n === 0) setIsDragging(false);
      return n;
    });
  }, []);

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(false); setDragCounter(0);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) processFiles(files);
  }, [processFiles]);

  // ============ PASTE ============
  useEffect(() => {
    const handlePaste = (e) => {
      if (!isModalOpen || compressing) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (file) {
            const ts = new Date().getTime();
            imageFiles.push(new File([file], `paste-${ts}.jpg`, { type: "image/jpeg" }));
          }
        }
      }
      if (imageFiles.length > 0) { e.preventDefault(); processFiles(imageFiles); }
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [isModalOpen, processFiles, compressing]);

  // ============ FILE INPUTS ============
  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) processFiles(files);
    e.target.value = null;
  };

  const handleCameraChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const cameraFiles = files.map(file => {
        const ts = new Date().getTime();
        return new File([file], `camera-${ts}.jpg`, { type: file.type || "image/jpeg" });
      });
      processFiles(cameraFiles);
    }
    e.target.value = null;
  };

  const removeSelectedFile = (index) => {
    URL.revokeObjectURL(previews[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
    setCompressInfo(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllFiles = () => {
    previews.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviews([]);
    setCompressInfo([]);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    previews.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviews([]);
    setCompressInfo([]);
  };

  const isQuotaFull = selectedFiles.length >= remainingSlots;
  const isProcessing = loading || compressing;

  const totalOriginal = compressInfo.reduce((sum, info) => sum + info.originalSize, 0);
  const totalCompressed = compressInfo.reduce((sum, info) => sum + info.compressedSize, 0);
  const totalSaved = totalOriginal - totalCompressed;
  const totalSavedPercent = totalOriginal > 0 ? Math.round((totalSaved / totalOriginal) * 100) : 0;

  const handleSubmitImage = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;
    setLoading(true);
    const formData = new FormData();
    selectedFiles.forEach((file) => { formData.append("images[]", file); });
    try {
      await PopImageService.create({ data: formData, popId: popId });
      refreshData();
      closeModal();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Gagal mengunggah gambar");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (id) => { setTargetDeleteId(id); setIsDeleteModalOpen(true); };

  const executeDelete = async () => {
    if (!targetDeleteId) return;
    setLoadingDelete(true);
    try {
      await PopImageService.delete({ id: targetDeleteId });
      setIsDeleteModalOpen(false);
      setOpenLightbox(false);
      if (refreshData) refreshData();
    } catch (error) {
      alert(error.response?.data?.message || "Gagal menghapus gambar");
    } finally {
      setLoadingDelete(false);
    }
  };

  // Toggle edit — saat edit dimatikan, refresh data
  const toggleEditLocation = () => {
    if (isEditLocation) {
      setIsEditLocation(false);
      refreshData?.();
    } else {
      setIsEditLocation(true);
    }
  };

  return (
    <>
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100" style={CARD_STYLE}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4 border-b pb-3" style={{ borderColor: "rgba(51,65,85,0.2)" }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: "rgba(239,68,68,0.1)" }}>
              <FiMapPin className="text-red-400" />
            </div>
            <h3 className="font-bold text-gray-200">Lokasi & Foto POP</h3>
          </div>
          <button
            type="button"
            onClick={toggleEditLocation}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all border ${
              isEditLocation
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                : "bg-cyan-500/10 border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20"
            }`}
          >
            {isEditLocation ? <FiCheck /> : <FiEdit3 />}
            {isEditLocation ? "Selesai" : "Edit Lokasi"}
          </button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ======== KOLOM KIRI: Foto + Alamat ======== */}
          <div className="space-y-3">
            {/* Grid Foto */}
            <div className="grid grid-cols-3 gap-2">
              {images?.map((item, idx) => (
                <div key={idx} className="relative group rounded-xl overflow-hidden aspect-square border border-slate-700/50 bg-slate-900/30">
                  <img src={BaseUrl + "/storage/" + item.image} alt={item.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => { setPhotoIndex(idx); setOpenLightbox(true); }}
                      className="p-2 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full text-white"
                    >
                      <FiZoomIn size={18} />
                    </button>
                  </div>
                </div>
              ))}

              {[...Array(remainingSlots)].map((_, i) => (
                <button
                  key={`add-${i}`}
                  onClick={() => setIsModalOpen(true)}
                  className="aspect-square border-2 border-dashed border-slate-700 rounded-xl flex flex-col items-center justify-center text-slate-500 hover:border-cyan-500/40 hover:text-cyan-400 transition-all"
                >
                  <div className="p-2 bg-slate-800 rounded-full">
                    <FiPlus size={20} />
                  </div>
                  <span className="text-[10px] font-bold mt-2 uppercase">Tambah</span>
                </button>
              ))}
            </div>

            {/* Alamat — hanya tampil saat TIDAK edit */}
            {!isEditLocation && (
              <div className="rounded-xl p-4 bg-slate-900/40 border border-slate-800">
                <label className="text-[9px] font-bold text-slate-500 uppercase mb-2 block">Alamat</label>
                <p className="text-sm text-slate-200 leading-relaxed">
                  {location?.address || "Belum ada alamat"}
                </p>
                {(location?.latitude && location?.longitude) && (
                  <p className="text-[10px] font-mono text-slate-600 mt-2">
                    {location.latitude}, {location.longitude}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* ======== KOLOM KANAN: PETA (Preview ATAU Edit) ======== */}
          <div className="rounded-xl overflow-hidden border border-slate-700/50 min-h-[300px] lg:min-h-[380px] relative">
            {isEditLocation ? (
              /* --- MODE EDIT: Peta interaktif Leaflet + Form --- */
              <FormLocationPopUpdate
                id={popId}
                data={location}
                setRefresh={refreshData}
                setOpenUpdate={setIsEditLocation}
              />
            ) : (
              /* --- MODE VIEW: Peta statis Google --- */
              <GoogleMapPreview lat={location?.latitude} lng={location?.longitude} />
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <ImagePreview
        open={openLightbox}
        images={images}
        photoIndex={photoIndex}
        setPhotoIndex={setPhotoIndex}
        onClose={() => setOpenLightbox(false)}
        onDelete={confirmDelete}
        loadingDelete={loadingDelete}
        baseUrl={BaseUrl}
      />

      {/* Modal Konfirmasi Hapus */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 rounded-2xl p-6 w-full max-w-xs shadow-2xl text-center border border-slate-700">
            <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiTrash2 size={30} />
            </div>
            <h3 className="text-lg font-bold text-gray-100 mb-2">Hapus Foto?</h3>
            <p className="text-sm text-slate-400 mb-6">Tindakan ini tidak dapat dibatalkan.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="flex-1 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-bold text-sm transition-all border border-slate-700"
              >
                Batal
              </button>
              <button
                onClick={executeDelete}
                disabled={loadingDelete}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2"
              >
                {loadingDelete ? <FiLoader className="animate-spin" /> : "Ya, Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Upload */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[99] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-slate-900 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-700">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-5 pb-0">
              <div>
                <h3 className="text-lg font-bold text-gray-100">Unggah Foto POP</h3>
                <p className="text-xs text-slate-400">Sisa kuota: {remainingSlots} foto</p>
              </div>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-200 p-1.5 rounded-lg bg-slate-800 border border-slate-700">
                <FiX size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitImage} className="p-5 space-y-4">
              {/* Hidden Inputs */}
              <input ref={galleryInputRef} type="file" multiple accept="image/*" onChange={handleGalleryChange} className="hidden" disabled={isQuotaFull || compressing} />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleCameraChange} className="hidden" disabled={isQuotaFull || compressing} />

              {/* Compressing Overlay */}
              {compressing && (
                <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center">
                  <div className="bg-slate-900 rounded-2xl p-6 shadow-2xl flex flex-col items-center gap-3 max-w-[200px] border border-slate-700">
                    <FiLoader className="animate-spin text-cyan-500" size={36} />
                    <div className="text-center">
                      <p className="text-sm font-bold text-gray-100">Mengompres...</p>
                      <p className="text-[11px] text-slate-400 mt-1">Memperkecil ukuran foto</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Drop Zone */}
              <div
                ref={dropZoneRef}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => { if (!isQuotaFull && !compressing && !isDragging) galleryInputRef.current?.click(); }}
                className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-300 cursor-pointer ${
                  isDragging
                    ? "border-cyan-500 bg-cyan-500/10 scale-[1.02]"
                    : isQuotaFull || compressing
                      ? "border-slate-700 bg-slate-800/50 cursor-not-allowed opacity-60"
                      : "border-slate-700 bg-slate-800/30 hover:border-cyan-500/50 hover:bg-cyan-500/5"
                }`}
              >
                {isDragging && (
                  <div className="absolute inset-0 rounded-2xl bg-cyan-500/10 border-2 border-cyan-500 flex flex-col items-center justify-center z-10">
                    <FiUpload className="text-cyan-400 mb-2" size={40} />
                    <p className="text-cyan-400 font-bold text-sm">Lepaskan di sini!</p>
                  </div>
                )}

                <div className={`flex flex-col items-center justify-center gap-2 ${isDragging ? "opacity-0" : ""} transition-opacity`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isQuotaFull ? "bg-slate-800" : "bg-cyan-500/10"}`}>
                    <FiUpload className={isQuotaFull ? "text-slate-600" : "text-cyan-500"} size={24} />
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${isQuotaFull ? "text-slate-500" : "text-slate-300"}`}>
                      {isQuotaFull ? "Kuota Penuh" : "Drag & drop atau klik"}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Semua format gambar diterima</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={isQuotaFull || compressing}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                    isQuotaFull || compressing
                      ? "bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700"
                      : "bg-slate-800 hover:bg-slate-700 text-slate-200 active:scale-95 border border-slate-700"
                  }`}
                >
                  <FiImage size={18} /> Galeri
                </button>
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={isQuotaFull || compressing}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                    isQuotaFull || compressing
                      ? "bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700"
                      : "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 active:scale-95 border border-emerald-500/20"
                  }`}
                >
                  <FiCamera size={18} /> Kamera
                </button>
              </div>

              {/* Info Tips */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "rgba(234,179,8,0.06)", border: "1px solid rgba(234,179,8,0.15)" }}>
                  <FiClipboard className="text-amber-500 flex-shrink-0" size={14} />
                  <p className="text-[10px] text-amber-400">
                    <span className="font-semibold">Paste:</span> <kbd className="px-1 py-0.5 bg-amber-500/10 rounded text-[9px] font-mono font-bold">Ctrl+V</kbd>
                  </p>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)" }}>
                  <FiUpload className="text-blue-400 flex-shrink-0" size={14} />
                  <p className="text-[10px] text-blue-400">
                    <span className="font-semibold">Auto-compress</span> aktif
                  </p>
                </div>
              </div>

              {/* Preview Area */}
              {previews.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                      <span className="w-5 h-5 bg-cyan-500/10 text-cyan-400 rounded-full flex items-center justify-center text-[10px] font-bold">
                        {previews.length}
                      </span>
                      Foto Siap Unggah
                    </label>
                    <button type="button" onClick={clearAllFiles} className="text-[11px] text-red-400 hover:text-red-300 font-semibold flex items-center gap-1">
                      <FiTrash2 size={12} /> Hapus Semua
                    </button>
                  </div>

                  {totalSaved > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.15)" }}>
                      <div className="w-6 h-6 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <FiCheck size={12} className="text-green-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-[11px] font-semibold text-green-400">Hemat {formatSize(totalSaved)} ({totalSavedPercent}%)</p>
                        <p className="text-[10px] text-green-500">{formatSize(totalOriginal)} → {formatSize(totalCompressed)}</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-2 p-3 rounded-xl" style={{ background: "rgba(15,23,42,0.4)", border: "1px solid rgba(51,65,85,0.2)" }}>
                    {previews.map((url, index) => (
                      <div key={index} className="relative group rounded-lg overflow-hidden border border-slate-700/50 aspect-square bg-slate-800">
                        <img src={url} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />

                        {compressInfo[index]?.percent > 0 && (
                          <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-green-500/90 rounded-md">
                            <span className="text-[8px] font-bold text-white">-{compressInfo[index].percent}%</span>
                          </div>
                        )}

                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5">
                          <p className="text-[8px] text-white/90 truncate">
                            {compressInfo[index]
                              ? `${formatSize(compressInfo[index].originalSize)} → ${formatSize(compressInfo[index].compressedSize)}`
                              : formatSize(selectedFiles[index]?.size)}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeSelectedFile(index); }}
                          className="absolute top-1 right-1 p-1 bg-red-500/90 hover:bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition-all hover:scale-110 shadow-lg"
                        >
                          <FiX size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isProcessing || selectedFiles.length === 0}
                className={`w-full font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 text-sm shadow-md ${
                  isProcessing || selectedFiles.length === 0
                    ? "bg-slate-800 text-slate-500 cursor-not-allowed shadow-none border border-slate-700"
                    : "bg-cyan-600 hover:bg-cyan-500 text-white active:scale-[0.98] shadow-cyan-600/20"
                }`}
              >
                {loading ? (
                  <><FiLoader className="animate-spin" size={18} /> Mengunggah...</>
                ) : (
                  <><FiUpload size={16} /> Unggah {selectedFiles.length} Foto</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};